import { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getCategoriesOptions, WorkflowStageTaskStatusEnum, WorkflowStageTaskStatusOptions } from '@generated';
import { WorkflowStageTaskCard } from '~/features/workflowing/tasks/WorkflowStageTaskCard';
import { combineSieveValues, type SieveFilterModel } from '~/util/sieve/filter';
import { Section } from '~/shared/section/Section';
import { Header3 } from '~/shared/components/text/Header3';
import { SelectField } from '~/shared/forms/controls/base-fields/SelectField';
import { DateField } from '~/shared/forms/controls/base-fields/DateField';
import { convertFromDateFieldValue, convertToDateFieldValue } from '~/util/date';
import { AutocompleteSearchField } from '~/shared/forms/controls/base-fields/AutocompleteSearchField';
import { ButtonGroupField } from '~/shared/forms/controls/base-fields/ButtonGroupField';
import { useIdentity } from '~/features/authentication/identity/IdentityContext';
import { CheckboxField } from '~/shared/forms/controls/base-fields/CheckboxField';
import { useSearch } from '~/util/http/useSearch';
import { useQuery } from '@tanstack/react-query';
import { useGetCategoryLabel } from '~/libraries/categories/useGetCategoryLabel';
import { Button } from '~/shared/components/buttons/Button';
import { Loading } from '~/shared/Loading';

type WorkflowStageTasksSortColumn = 'dueAt' | 'title' | 'category' | 'workflowStageTaskStatusLabel';

interface WorkflowStageTasksFiltersAndSort {
	sortColumn: WorkflowStageTasksSortColumn;
	sortDirection: 'asc' | 'desc';
	workflowStageTaskStatusIds: number[];
	categoryNames: string[];
	assignedToUserId?: string;
	assignedToMe: boolean;
	dueDateFrom?: Date;
	dueDateTo?: Date;
	mySubscriptionsOnly: boolean;
	stayId?: string;
}

export function WorkflowStageTaskList({ stayId }: { stayId?: string }) {
	const { loggedInUser } = useIdentity();
	const { data: categories } = useQuery(getCategoriesOptions());
	const getCategoryLabel = useGetCategoryLabel();
	const subscribedWorkflowStageTaskDefinitionIds = loggedInUser ? loggedInUser.subscriptions.map(x => x.workflowStageTaskDefinitionId) : [];

	const [filters, setFilters] = useState<WorkflowStageTasksFiltersAndSort>({
		sortColumn: 'dueAt',
		sortDirection: 'asc',
		workflowStageTaskStatusIds: [WorkflowStageTaskStatusEnum.NeedsCompleted],
		categoryNames: [],
		assignedToMe: true,
		assignedToUserId: loggedInUser.id,
		mySubscriptionsOnly: true
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const clearFilters = () => {
		setFilters({
			sortColumn: 'dueAt',
			sortDirection: 'asc',
			workflowStageTaskStatusIds: [],
			categoryNames: [],
			assignedToMe: true,
			assignedToUserId: loggedInUser.id,
			mySubscriptionsOnly: true
		});
	};

	const sieveFilters: SieveFilterModel[] = [{ name: 'constrainedToVisit', value: 'false', operator: '==' }];

	if (stayId) {
		sieveFilters.push({ name: 'stayId', value: stayId, operator: '==' });
	}

	if (filters.workflowStageTaskStatusIds.length > 0) {
		sieveFilters.push({
			name: 'workflowStageTaskStatusId',
			value: combineSieveValues(filters.workflowStageTaskStatusIds),
			operator: '=='
		});
	}

	if (filters.categoryNames.length > 0) {
		sieveFilters.push({
			name: 'category',
			value: combineSieveValues(filters.categoryNames),
			operator: '=='
		});
	}

	if (filters.assignedToUserId) {
		sieveFilters.push({
			name: 'assignedToId',
			value: filters.assignedToUserId,
			operator: '=='
		});
	}

	if (filters.dueDateFrom) {
		sieveFilters.push({
			name: 'dueAt',
			value: filters.dueDateFrom,
			operator: '>='
		});
	}

	if (filters.dueDateTo) {
		sieveFilters.push({
			name: 'dueAt',
			value: filters.dueDateTo,
			operator: '<='
		});
	}

	if (filters.mySubscriptionsOnly && subscribedWorkflowStageTaskDefinitionIds.length > 0) {
		sieveFilters.push({
			name: 'workflowStageTaskDefinitionId',
			value: combineSieveValues(subscribedWorkflowStageTaskDefinitionIds),
			operator: '=='
		});
	}

	const hasActiveFilters =
		filters.workflowStageTaskStatusIds.length > 0 ||
		filters.categoryNames.length > 0 ||
		!!filters.assignedToUserId ||
		!!filters.dueDateFrom ||
		!!filters.dueDateTo ||
		filters.mySubscriptionsOnly;

	const {
		data: workflowStageTasks,
		isLoading,
		count
	} = useSearch({
		searchType: 'workflow-stage-task',
		sortColumn: filters.sortColumn,
		sortDirection: filters.sortDirection,
		filters: sieveFilters,
		page: currentPage,
		pageSize: pageSize,
		staleTime: 30000,
		gcTime: 30000
	});

	const totalPages = Math.ceil((count ?? 0) / pageSize);

	const toggleStatus = (status: number) => {
		if (filters.workflowStageTaskStatusIds.length === 1 && filters.workflowStageTaskStatusIds[0] === Number(status)) {
			return;
		}
		setFilters(prev => ({
			...prev,
			workflowStageTaskStatusIds: prev.workflowStageTaskStatusIds.includes(Number(status))
				? prev.workflowStageTaskStatusIds.filter(id => id !== Number(status))
				: [...prev.workflowStageTaskStatusIds, Number(status)]
		}));
	};

	const toggleCategory = (category: string) => {
		setFilters(prev => ({
			...prev,
			categoryNames: prev.categoryNames.includes(category) ? prev.categoryNames.filter(c => c !== category) : [...prev.categoryNames, category]
		}));
	};

	const toggleAssignedToMe = () => {
		setFilters(prev => {
			if (prev.assignedToMe) {
				return { ...prev, assignedToMe: false, assignedToUserId: undefined };
			} else {
				return { ...prev, assignedToMe: true, assignedToUserId: loggedInUser.id };
			}
		});
	};

	const handlePageSizeChange = (newSize: number) => {
		setPageSize(newSize);
		setCurrentPage(1);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const goToNextPage = () => {
		if (currentPage < totalPages) {
			setCurrentPage(prev => prev + 1);
		}
	};

	const goToPreviousPage = () => {
		if (currentPage > 1) {
			setCurrentPage(prev => prev - 1);
		}
	};

	return (
		<Section>
			<div className="grid grid-cols-1 gap-4">
				<div className="flex flex-col px-2">
					<div className="space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
						<div className="flex items-center justify-between">
							<Header3 className="text-sm font-semibold text-gray-900">Filter & Sort</Header3>
							{hasActiveFilters && (
								<button
									onClick={clearFilters}
									type="button"
									className="flex items-center gap-1 text-xs font-medium text-primary-500 transition-colors hover:text-primary-500/80"
								>
									<X className="size-3" />
									Clear all
								</button>
							)}
						</div>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<div>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<SelectField
										label="Sort by"
										value={filters.sortColumn ?? null}
										onChange={sortColumn =>
											setFilters(prev => ({ ...prev, sortColumn: sortColumn as WorkflowStageTasksSortColumn }))
										}
										items={[
											{ id: 'dueAt', name: 'Due Date' },
											{ id: 'title', name: 'Title' },
											{ id: 'category', name: 'Category' },
											{ id: 'workflowStageTaskStatusLabel', name: 'Status' }
										]}
										containerClassName="w-[100%]"
									/>
									<SelectField
										label="Sort order"
										value={filters.sortDirection ?? null}
										onChange={sortDirection => setFilters(prev => ({ ...prev, sortDirection: sortDirection as 'asc' | 'desc' }))}
										items={[
											{ id: 'asc', name: 'Ascending' },
											{ id: 'desc', name: 'Descending' }
										]}
									/>
								</div>
							</div>
							<div className="grid grid-cols-2 gap-2">
								<DateField
									label="Due Date From"
									value={filters.dueDateFrom ? convertToDateFieldValue(filters.dueDateFrom) : null}
									onChange={date =>
										setFilters(prev => ({ ...prev, dueDateFrom: date ? convertFromDateFieldValue(date)! : undefined }))
									}
								/>
								<DateField
									label="Due Date To"
									value={filters.dueDateTo ? convertToDateFieldValue(filters.dueDateTo) : null}
									onChange={date =>
										setFilters(prev => ({ ...prev, dueDateTo: date ? convertFromDateFieldValue(date)! : undefined }))
									}
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
							<ButtonGroupField
								options={WorkflowStageTaskStatusOptions.map(option => ({ id: option.id, label: option.label }))}
								isSelected={(id: number) => filters.workflowStageTaskStatusIds.includes(id)}
								onSelect={toggleStatus}
								label="Status"
								variant="chips"
							/>
						</div>
						<div className="grid grid-cols-2 gap-2">
							<AutocompleteSearchField
								searchType="user"
								value={filters.assignedToUserId ?? null}
								onChange={(value: string | null) => {
									setFilters(prev => ({ ...prev, assignedToUserId: value ?? undefined }));
								}}
								isDisabled={filters.assignedToMe}
								placeholder="Search by name..."
								label="Assigned To"
							/>
							<CheckboxField
								className="mt-5"
								value={filters.assignedToMe}
								onChange={toggleAssignedToMe}
								label="Assigned to me"
								hideLabel
							/>
						</div>
						{categories && (
							<ButtonGroupField
								options={categories.filter(x => x.subscribable).map(x => ({ id: x.name, label: getCategoryLabel(x.name) }))}
								isSelected={(id: string) => filters.categoryNames.includes(id)}
								onSelect={toggleCategory}
								label="Category"
								variant="chips"
							/>
						)}
						{subscribedWorkflowStageTaskDefinitionIds.length === 0 ? (
							<p className="text-sm text-yellow-600">
								You are not subscribed to any task definitions. Please update your subscriptions to see tasks assigned to you.
							</p>
						) : (
							<CheckboxField
								label="My Subscriptions Only"
								value={filters.mySubscriptionsOnly}
								onChange={selected => {
									setFilters(prev => ({ ...prev, mySubscriptionsOnly: selected }));
								}}
							/>
						)}
					</div>
				</div>

				<div className="flex-1 space-y-3 overflow-y-auto p-2">
					{isLoading ? (
						<Loading />
					) : workflowStageTasks.length === 0 ? (
						<div className="py-12 text-center">
							<p className="text-gray-500">No tasks found matching your filters.</p>
						</div>
					) : (
						workflowStageTasks.map(task => <WorkflowStageTaskCard key={task.id} task={task} />)
					)}
				</div>

				{workflowStageTasks.length > 0 && (
					<div className="border-t border-gray-200 bg-white px-4 py-3">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<span className="text-sm text-gray-700">
									Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, workflowStageTasks.length)}{' '}
									of&nbsp;
									{count} tasks
								</span>
								<div className="flex items-center gap-2">
									<label htmlFor="pageSize" className="text-sm text-gray-600">
										Per page:
									</label>
									<select
										id="pageSize"
										value={pageSize}
										onChange={e => handlePageSizeChange(Number(e.target.value))}
										className="rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
									>
										<option value={5}>5</option>
										<option value={10}>10</option>
										<option value={20}>20</option>
									</select>
								</div>
							</div>

							<div className="flex items-center gap-2">
								<Button
									onClick={goToPreviousPage}
									isDisabled={currentPage === 1}
									Icon={ChevronLeft}
									appearance="ghost"
									intent="neutral"
								/>

								<div className="flex items-center gap-1">
									{Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
										if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
											return (
												<Button
													key={page}
													onClick={() => handlePageChange(page)}
													intent={currentPage === page ? 'primary' : 'neutral'}
													appearance="outline"
													label={page.toString()}
												/>
											);
										} else if (page === currentPage - 2 || page === currentPage + 2) {
											return (
												<span key={page} className="px-2 text-gray-400">
													...
												</span>
											);
										}
										return null;
									})}
								</div>
								<Button
									onClick={goToNextPage}
									isDisabled={currentPage === totalPages}
									Icon={ChevronRight}
									appearance="ghost"
									intent="neutral"
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</Section>
	);
}
